// Оптимистичные правки кэша каталога (фаза 3, этап B).
//
// Правка кабинета уходит в очередь (`shared/offline/cabinetQueue`), а на сервер —
// когда будет связь. Чтобы человек увидел результат СРАЗУ (иначе офлайн-правка
// выглядит как «ничего не произошло»), тот же результат применяется к кэшу
// TanStack Query здесь. Снимок кэша (этап A) сохраняет его на диск, поэтому
// правка переживает и перезапуск без сети.
//
// После успешного флаша списки инвалидируются и правда сервера вытесняет
// оптимистичную копию. При отказе сервера копия ОСТАЁТСЯ, а рядом горит ошибка —
// стирать сделанную работу молча нельзя.

import type { QueryClient } from '@tanstack/react-query';

import type { CabinetEntity } from '@/shared/offline';

import type { Category } from './types';

/**
 * Только сущности КАТАЛОГА — не весь `CabinetEntity`. Другие домены (например,
 * персонал) правят свой кэш своим способом; связывать этот файл с их ключами
 * значило бы тащить сюда знание о чужих модулях при каждом расширении списка.
 */
type CatalogCabinetEntity = Extract<
  CabinetEntity,
  'category' | 'masterProduct' | 'locationProduct'
>;

const PREFIX: Record<CatalogCabinetEntity, readonly [string, string]> = {
  category: ['catalog', 'categories'],
  masterProduct: ['catalog', 'master-products'],
  locationProduct: ['catalog', 'location-products'],
};

type AnyRow = { id: string } & Record<string, unknown>;

function mapRows(data: unknown, fn: (rows: AnyRow[]) => AnyRow[]): unknown {
  if (Array.isArray(data)) {
    return fn([...(data as AnyRow[])]);
  }
  if (data && typeof data === 'object' && 'results' in data) {
    const page = data as { results: readonly AnyRow[] };
    return { ...page, results: fn([...page.results]) };
  }
  return data;
}

/** Применить преобразование ко всем закэшированным спискам сущности. */
function patchLists(
  client: QueryClient,
  entity: CatalogCabinetEntity,
  fn: (rows: AnyRow[]) => AnyRow[],
): void {
  const matches = client.getQueryCache().findAll({ queryKey: PREFIX[entity] });
  for (const query of matches) {
    const next = mapRows(query.state.data, fn);
    if (next !== query.state.data) {
      client.setQueryData(query.queryKey, next);
    }
  }
}

/**
 * Эталоны, только что добавленные в меню точки без сети (T-045): в списках каталога,
 * размеченных для ЭТОЙ точки, они сразу «уже в меню» и не новые. Строка уходит из блока
 * «Новое в каталоге сети», а модал показывает её «уже в меню» — не дожидаясь связи.
 *
 * Только списки с `location` этой точки: разметка — свойство пары «эталон ↔ точка»,
 * и у соседней точки того же менеджера эти позиции по-прежнему новые.
 */
export function markAddedToMenu(
  client: QueryClient,
  location: string,
  masterIds: readonly string[],
): void {
  const ids = new Set(masterIds);
  for (const query of client.getQueryCache().findAll({ queryKey: PREFIX.masterProduct })) {
    const filters = query.queryKey[2] as { readonly location?: unknown } | undefined;
    if (filters?.location !== location) {
      continue;
    }
    const next = mapRows(query.state.data, (rows) =>
      rows.map((row) => (ids.has(row.id) ? { ...row, is_new: false, menu_status: 'active' } : row)),
    );
    if (next !== query.state.data) {
      client.setQueryData(query.queryKey, next);
    }
  }
}

export type OptimisticInput =
  | { entity: CatalogCabinetEntity; action: 'create'; row: AnyRow }
  | { entity: CatalogCabinetEntity; action: 'update'; id: string; patch: Record<string, unknown> }
  | { entity: CatalogCabinetEntity; action: 'delete'; id: string };

export function applyOptimistic(client: QueryClient, input: OptimisticInput): void {
  if (input.action === 'create') {
    patchLists(client, input.entity, (rows) =>
      rows.some((r) => r.id === input.row.id) ? rows : [input.row, ...rows],
    );
    return;
  }
  if (input.action === 'update') {
    patchLists(client, input.entity, (rows) =>
      rows.map((r) => (r.id === input.id ? { ...r, ...input.patch } : r)),
    );
    return;
  }
  // delete: продукт точки сервер архивирует — из списка активных он уходит;
  // категории и эталоны удаляются целиком.
  patchLists(client, input.entity, (rows) => rows.filter((r) => r.id !== input.id));
}

function rowsOf(data: unknown): AnyRow[] {
  if (Array.isArray(data)) return data as AnyRow[];
  if (data && typeof data === 'object' && 'results' in data) {
    return (data as { results: readonly AnyRow[] }).results as AnyRow[];
  }
  return [];
}

/** Найти строку сущности в любом закэшированном списке по id. */
export function findRow(
  client: QueryClient,
  entity: CatalogCabinetEntity,
  id: string,
): AnyRow | undefined {
  for (const query of client.getQueryCache().findAll({ queryKey: PREFIX[entity] })) {
    const hit = rowsOf(query.state.data).find((row) => row.id === id);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * Найти категорию в кэше по id — списки продуктов рендерят `category_detail`
 * (цвет, срок), и синтетическая строка без него уронит отрисовку.
 */
export function findCategory(client: QueryClient, id: string): Category | undefined {
  return findRow(client, 'category', id) as Category | undefined;
}

const NOW = () => new Date().toISOString();

/** Синтетическая строка новой категории для оптимистичного показа. */
export function draftCategory(id: string, body: Partial<Category>): AnyRow {
  return {
    id,
    name: body.name ?? '',
    unit: body.unit ?? 'hours',
    value: body.value ?? 24,
    color: body.color ?? '#888888',
    is_global: false,
    // Полосу срока считает сервер из unit+value; у синтетической строки её нет,
    // до синка чип пустой (а не падает на `.label` у undefined).
    shelf_life_band: { code: '', label: '—' },
    usage_count: { master_products: 0, location_products: 0, total: 0 },
    created_at: NOW(),
    updated_at: NOW(),
    _pending: true,
  };
}

/** Синтетическая строка нового продукта меню точки. */
export function draftLocationProduct(
  id: string,
  location: string,
  body: Record<string, unknown>,
  categoryDetail: Category | undefined,
): AnyRow {
  return {
    id,
    location,
    master_product: null,
    name: (body.name as string) ?? '',
    category: (body.category as string) ?? '',
    category_detail: categoryDetail,
    product_type: (body.product_type as string | null) ?? null,
    product_type_detail: null,
    storage_conditions: (body.storage_conditions as string) ?? '',
    sku: (body.sku as string) ?? '',
    photo_url: '',
    status: 'active',
    is_local: true,
    is_locally_modified: false,
    created_at: NOW(),
    updated_at: NOW(),
    _pending: true,
  };
}

/** Синтетическая строка нового эталона родительского каталога. */
export function draftMasterProduct(
  id: string,
  body: Record<string, unknown>,
  categoryDetail: Category | undefined,
): AnyRow {
  return {
    id,
    name: (body.name as string) ?? '',
    default_category: (body.default_category as string) ?? '',
    default_category_detail: categoryDetail,
    product_type: (body.product_type as string | null) ?? null,
    product_type_detail: null,
    storage_conditions: (body.storage_conditions as string) ?? '',
    sku: (body.sku as string) ?? '',
    photo_url: '',
    status: 'active',
    is_global: false,
    used_in_locations_count: 0,
    created_at: NOW(),
    updated_at: NOW(),
    _pending: true,
  };
}
